'use client';

import React, { createContext, useContext, useState, ReactNode } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { motion } from 'motion/react';
import { IPTVPlayer } from './IPTVPlayer';
import { Maximize2, X, Volume2, VolumeX, GripHorizontal } from 'lucide-react';

interface Channel {
  url: string;
  name: string;
}

interface GlobalPlayerContextType {
  playedChannel: Channel | null;
  setPlayedChannel: React.Dispatch<React.SetStateAction<Channel | null>>;
  isPremium: boolean;
  setIsPremium: (val: boolean) => void;
}

const GlobalPlayerContext = createContext<GlobalPlayerContextType | null>(null);

export const useGlobalPlayer = () => {
  const context = useContext(GlobalPlayerContext);
  if (!context) return null; // Safe fallback if not wrapped
  return context;
};

export const GlobalPlayerProvider = ({ children }: { children: ReactNode }) => {
  const [playedChannel, setPlayedChannel] = useState<Channel | null>(null);
  const [isPremium, setIsPremium] = useState(false);
  const [pipMuted, setPipMuted] = useState(true); // Default mute to prevent unexpected background sounds
  
  const pathname = usePathname();
  const router = useRouter();

  const isLivePage = pathname === '/live';
  const showGlobalPiP = playedChannel !== null && !isLivePage;

  return (
    <GlobalPlayerContext.Provider value={{ playedChannel, setPlayedChannel, isPremium, setIsPremium }}>
      {children}
      
      {/* IN-WEBSITE PiP UI */}
      {showGlobalPiP && (
        <motion.div 
          drag 
          dragMomentum={false}
          className="fixed bottom-6 right-6 w-72 md:w-80 bg-gray-900 rounded-2xl shadow-[0_0_40px_rgba(0,0,0,0.5)] border border-gray-700 overflow-hidden z-[9999] flex flex-col will-change-transform"
        >
           {/* Top Controls Bar */}
           <div className="flex items-center justify-between px-3 py-2 bg-gray-950 border-b border-gray-800">
              <div className="flex items-center gap-2 cursor-grab active:cursor-grabbing text-gray-500 hover:text-gray-300" title="Drag to move">
                <GripHorizontal size={16} />
              </div>
              <div className="flex items-center gap-1">
                 <button 
                   onClick={() => setPipMuted(!pipMuted)}
                   className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
                   title={pipMuted ? "Unmute" : "Mute"}
                 >
                   {pipMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                 </button>
                 <button 
                   onClick={() => setPlayedChannel(null)}
                   className="p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-500/10 transition-colors"
                   title="Close PiP"
                 >
                   <X size={16} />
                 </button>
              </div>
           </div>

           <div className="h-40 md:h-44 bg-black relative group cursor-pointer" onClick={() => router.push('/live')}>
             <div className="absolute inset-0 pointer-events-none">
               <IPTVPlayer 
                  url={playedChannel.url} 
                  title={playedChannel.name}
                  autoPlay={true}
                  hideControls={true}
                  muted={pipMuted}
                  isPremium={isPremium}
               />
             </div>
             
             {/* Overlay Controls */}
             <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                <div className="w-12 h-12 bg-indigo-600 rounded-full flex items-center justify-center text-white shadow-lg">
                  <Maximize2 size={24} />
                </div>
             </div>
           </div>
           
           <div className="p-3 bg-gray-900 flex items-center justify-between border-t border-gray-800">
              <div className="min-w-0 pr-2">
                 <div className="text-[10px] text-red-500 font-bold uppercase tracking-widest mb-0.5 flex items-center gap-1.5">
                   <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                   Playing Background
                 </div>
                 <h4 className="text-sm font-bold text-white truncate">{playedChannel.name}</h4>
              </div>
           </div>
        </motion.div>
      )}
    </GlobalPlayerContext.Provider>
  );
};
